import { Inject, Injectable, OnDestroy } from '@angular/core';
import { IHotelsResponse } from '@app/models/hotels_response_model';
import { environment } from '@env/environment';
import { DalService } from '@upr-web-primer/dal';
import { MsGuestsService } from '@upr-web-primer/ms-guests';
import { UdxSpinnerService } from '@upr-web-primer/udx-spinner';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class HotelsService implements OnDestroy {
  hotelsData$ = new BehaviorSubject<IHotelsResponse[]>([]);
  isDestroyed$ = new Subject<boolean>();

  constructor(
    @Inject('RelativeUrls') private readonly relativeUrls: any,
    @Inject('AppDefaults') private readonly appDefaults: any,
    private readonly dal: DalService,
    private readonly guestsService: MsGuestsService,
    private readonly spinner: UdxSpinnerService,
  ) {
    const showUpcomingHotelReservations =
      environment.config?.dashboard?.showUpcomingHotelReservations;
    const isResort = environment.config?.dashboard?.showVacationPackages;
    this.guestsService.helper.loggedInStatus$
      .pipe(takeUntil(this.isDestroyed$))
      .subscribe((isLoggedIn) => {
        this.handleLoginStatusChange(
          isLoggedIn,
          showUpcomingHotelReservations,
          isResort,
        );
      });
  }

  async getHotels(): Promise<IHotelsResponse[]> {
    this.spinner.hide();
    const url = this.relativeUrls.HOTELS_DATA_URL.replace(
      '{resort-area-code}',
      this.appDefaults.RESORT_AREA_CODE,
    );
    try {
      const result = (await this.dal.httpRequest(
        url,
        'GET',
        {},
      )) as IHotelsResponse[];
      this.hotelsData$.next(result);
      this.spinner.hide();
      return result;
    } catch (error) {
      console.error('Error fetching hotel data:', error);
      this.spinner.hide();
      return Promise.reject(
        error instanceof Error ? error : new Error(String(error)),
      );
    }
  }

  private handleLoginStatusChange(
    isLoggedIn: boolean,
    showUpcomingHotelReservations: boolean,
    isResort: boolean,
  ): void {
    const handlers = this.getLoginStatusHandlers(
      showUpcomingHotelReservations,
      isResort,
    );
    const handler = handlers[isLoggedIn ? 'loggedIn' : 'loggedOut'];
    handler();
  }

  private getLoginStatusHandlers(
    showUpcomingHotelReservations: boolean,
    isResort: boolean,
  ): { loggedIn: () => void; loggedOut: () => void } {
    return {
      loggedIn: () =>
        this.handleUserLogin(showUpcomingHotelReservations, isResort),
      loggedOut: () => this.handleUserLogout(),
    };
  }

  private handleUserLogin(
    showUpcomingHotelReservations: boolean,
    isResort: boolean,
  ): void {
    if (showUpcomingHotelReservations || isResort) {
      this.getHotels();
    }
  }

  private handleUserLogout(): void {
    this.hotelsData$.next([]);
  }

  ngOnDestroy(): void {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
}
